import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, tap } from 'rxjs';
import { environment } from '../../../environments/environment';
import { User, LoginResponse, ApiResponse } from '../../shared/models';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly apiUrl = `${environment.apiUrl}/auth`;
  private currentUserSubject = new BehaviorSubject<Partial<User> | null>(null);
  currentUser$ = this.currentUserSubject.asObservable();

  constructor(private http: HttpClient) {
    const stored = localStorage.getItem('ltng_user');
    if (stored) {
      try {
        this.currentUserSubject.next(JSON.parse(stored));
      } catch {
        localStorage.removeItem('ltng_user');
      }
    }
    if (this.token && !this.isTokenValid()) {
      this.logout();
    }

    // Cross-tab session sync (storage events do not fire in the same tab)
    if (typeof window !== 'undefined') {
      window.addEventListener('storage', (event) => {
        if (event.key !== 'ltng_user' && event.key !== 'ltng_token') {
          return;
        }
        this.syncSessionFromStorage();
      });
    }
  }

  /** Re-read token/user from localStorage after another tab changes them. */
  private syncSessionFromStorage(): void {
    const token = localStorage.getItem('ltng_token');
    const userStr = localStorage.getItem('ltng_user');

    if (!token || !userStr) {
      this.currentUserSubject.next(null);
      return;
    }

    if (!this.isTokenValid()) {
      localStorage.removeItem('ltng_token');
      localStorage.removeItem('ltng_user');
      this.currentUserSubject.next(null);
      return;
    }

    try {
      this.currentUserSubject.next(JSON.parse(userStr));
    } catch {
      localStorage.removeItem('ltng_user');
      this.currentUserSubject.next(null);
    }
  }

  get token(): string | null {
    return localStorage.getItem('ltng_token');
  }

  get currentUser(): Partial<User> | null {
    return this.currentUserSubject.value;
  }

  /** Returns true when a non-expired JWT is present in storage. */
  isTokenValid(): boolean {
    const token = this.token;
    if (!token) return false;

    const payload = this.decodeJwtPayload(token);
    if (!payload || typeof payload['exp'] !== 'number') {
      return false;
    }

    return payload['exp'] * 1000 > Date.now();
  }

  get isLoggedIn(): boolean {
    if (!this.isTokenValid()) {
      if (this.token) {
        this.logout();
      }
      return false;
    }
    return true;
  }

  private decodeJwtPayload(token: string): Record<string, unknown> | null {
    try {
      const parts = token.split('.');
      if (parts.length !== 3) return null;

      const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
      const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
      const json = atob(padded);
      return JSON.parse(json) as Record<string, unknown>;
    } catch {
      return null;
    }
  }

  login(phone: string, password: string): Observable<ApiResponse<LoginResponse>> {
    return this.http.post<ApiResponse<LoginResponse>>(`${this.apiUrl}/login`, { phone, password }).pipe(
      tap((res) => {
        if (res.success && res.data) {
          localStorage.setItem('ltng_token', res.data.token);
          localStorage.setItem('ltng_user', JSON.stringify(res.data.user));
          this.currentUserSubject.next(res.data.user);
        }
      }),
    );
  }

  logout(): void {
    localStorage.removeItem('ltng_token');
    localStorage.removeItem('ltng_user');
    this.currentUserSubject.next(null);
  }

  me(): Observable<ApiResponse<User>> {
    return this.http.get<ApiResponse<User>>(`${this.apiUrl}/me`);
  }

  changePassword(current_password: string, new_password: string): Observable<ApiResponse<{ message: string }>> {
    return this.http.post<ApiResponse<{ message: string }>>(`${this.apiUrl}/change-password`, {
      current_password,
      new_password,
    });
  }

  hasRole(role: string): boolean {
    return this.currentUser?.role === role;
  }
}
